import { useState } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

// Creates a plan-less free-draw sheet and lands in the takeoff editor on it.
export function NewBlankSheetDialog({
  projectId,
  trigger,
}: {
  projectId: number
  /** Custom trigger button; defaults to a secondary "New blank sheet". */
  trigger?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const form = useForm({ label: "" })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(`/projects/${projectId}/blank_sheets`, {
      // The redirect can land back on the same page component (the takeoff
      // editor), where Inertia preserves mounted state — close explicitly.
      onSuccess: () => {
        setOpen(false)
        form.reset()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="secondary" size="sm" data-testid="new-blank-sheet">
            New blank sheet
          </Button>
        )}
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>New blank sheet</DialogTitle>
          <DialogDescription>
            A free-draw sheet with a 1-ft grid at a preset scale — for field
            measures with no plan. Rooms you trace are true-scale immediately.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label htmlFor="blank-sheet-label">Label</label>
            <Input
              id="blank-sheet-label"
              value={form.data.label}
              onChange={(e) => form.setData("label", e.target.value)}
              placeholder="e.g. Field measure — 1st floor"
              className="mt-1"
              autoFocus
            />
            {form.errors.label && <p className="mt-1 text-sm text-danger">{form.errors.label}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.processing || form.data.label.trim() === ""}>
              Create &amp; open
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
